import docOperationApi from "../Services/doc-operation-service-api";
import docProfileApi from "../Services/doc-profile-api";
import DashboardOrchService, {
  getUserAbridged,
} from "../Services/DashboardOrchService";
import type { UserAbridged } from "@/types/AbridgedResponse";
import { getPdfSimpliBaseURL } from "../Services/base-url/api-constants";
import { triggerDownload, triggerDownloadBlob } from "../hooks/useAudioDownload";
import {
  CategoryName,
  DocOperationType,
  DocOperationFileType,
} from "./constant";
import { fetchBlobFromResultPath, fetchJsonFromResultPath } from "./musicBlobFetch";
import { parseMusicMetadataBlob } from "./compositionPlanBlob";
import type { GenerateMusicResult } from "./generateMusic";
import {
  getVisitGuidForTracking,
} from "@/utils/visitor";
import {
  createCookie,
  createUUID,
  getBlobContainer,
  getBlobFileName,
  getCleanUserGuid,
  getFileExtension,
  getLinkWithLocale,
  getUserContainer,
  isUserLoggedIn,
  normalizeGuid,
  PerquisiteBountyEnable,
  readCookie,
  resolveAppLocale,
} from "./helper";
import { blobSignature, getBlobUri, getTenantId, sasTokenExpiryHours } from "./general-config";
import { saveMusicEditorQueryInfo, getMusicGenerationCustomData, registerMusicGenerationCustomData } from "./musicEditorQuery";
import { fetchUserProfileAbridged } from "./userProfile";
import {
  hasMusicEditorCustomData,
  mergeMusicEditorCustomData,
  parseCustomDataFromDocProfileCustomProps,
  parseCustomDataFromMetadataBlob,
  parseDocProfileMetadataCustomProps,
  buildEditorRestoreFromCustomData,
  parseLyricsSectionsFromCustomData,
  serializeCustomDataForDocProfile,
  normalizeDocProfileForApi,
} from "./musicCustomData";
import {
  audioMimeTypeForFormat,
  resolveGeneratedAudioFormat,
} from "./musicDisplay";
import { extractStemArchive } from "./extractStemArchive";
import type { StemsZipRef } from "./jobResultBlob";
import { parseMusicBlobRefFromPath } from "./jobResultBlob";
import { overwriteMusicBlob } from "./musicBlobUpload";
import { reportApiError } from "./errorPopupBridge";
import { DocOperationJobStatusWithInterval } from "../components/action";
import { getDocOperationResultUrls } from "./jobResultBlob";
import {
  resolveFilenameFromStorageUrl,
  resolveMusicBlobStorageRef,
  resolveMusicJobIdFromCustomProps,
  resolveMusicJobIdFromDocProfile,
  resolveDisplayNameFromDocProfile,
  resolveBlobFileNameFromDocProfile,
  buildMusicBlobStorageFileName,
  isMusicBlobStorageFileName,
  type MusicStorageRef,
} from "./musicStorage";

export type { MusicStorageRef };

export {
  resolveFilenameFromStorageUrl,
  resolveMusicBlobStorageRef,
  resolveMusicJobIdFromCustomProps,
  resolveMusicJobIdFromDocProfile,
  resolveDisplayNameFromDocProfile,
  resolveBlobFileNameFromDocProfile,
  buildMusicBlobStorageFileName,
  isMusicBlobStorageFileName,
};

/** Resolves the pdfsimpliusers-relative path used by dashboard / querydata. */
export function resolveMusicStorageRef(
  music: Pick<
    GenerateMusicResult,
    "musicId" | "filename" | "blobPath" | "blobContainer"
  >,
): MusicStorageRef {
  return resolveMusicBlobStorageRef({
    musicId: music.musicId,
    blobPath: music.blobPath,
    blobContainer: music.blobContainer,
  });
}

const BLOB_SAS_COOKIE_NAME = "sast";

/** One doc-profile POST per music job per session (matches TTS). */
const registeredDocProfileIds = new Map<string, string>();

export function clearRegisteredMusicDocProfile(musicId?: string): void {
  if (musicId) {
    registeredDocProfileIds.delete(musicId);
    return;
  }
  registeredDocProfileIds.clear();
}

export function getRegisteredMusicDocProfileId(
  musicId: string,
): string | undefined {
  return registeredDocProfileIds.get(musicId);
}

/** Doc-operation audio format code (mp3=110, wav=108, etc.). */
export function getDocOperationFileType(ext: string): number {
  const code = DocOperationFileType[ext.toLowerCase()];
  if (code === undefined) {
    throw new Error(`Unsupported doc-operation format: ${ext}`);
  }
  return code;
}

function getLocaleForRedirect(): string {
  return resolveAppLocale();
}

function getPdfSimpliAppUrl(): string {
  return `${getPdfSimpliBaseURL}/app`;
}

function redirectTo(path: string): void {
  window.location.assign(`${getPdfSimpliAppUrl()}${path}`);
}

function normalizeSasToken(value: string): string {
  if (!value) return "";
  if (value.startsWith("http")) {
    return new URL(value).search;
  }
  return value.startsWith("?") ? value : `?${value}`;
}

export async function getSasTokenForBlobAccess(): Promise<string> {
  const cachedToken = readCookie(BLOB_SAS_COOKIE_NAME);
  if (cachedToken) {
    return normalizeSasToken(cachedToken);
  }

  try {
    const response = await DashboardOrchService.generateSasUriTokenForContainer(
      sasTokenExpiryHours,
    );
    const sasToken = normalizeSasToken(response);
    createCookie(BLOB_SAS_COOKIE_NAME, sasToken, 1);
    return sasToken;
  } catch {
    return blobSignature();
  }
}

function isPremiumForDownload(user: UserAbridged): boolean {
  return !!(user.isPremiumUser || user.parentUserGuid);
}

/** Matches TTS — requires an auth cookie; accepts refreshToken for manual dev setups. */
function isLoggedInForDownload(): boolean {
  return !!(readCookie("jwtToken") || readCookie("refreshToken"));
}

async function resolvePremiumForDownload(
  user: UserAbridged,
): Promise<boolean> {
  if (isPremiumForDownload(user)) {
    return true;
  }

  if (user.perquisiteBountyKey && user.userGuid) {
    return PerquisiteBountyEnable(user.perquisiteBountyKey, user.userGuid);
  }

  return false;
}

async function recordDownloadDate(
  docId: string | undefined,
  blobFileName: string,
): Promise<void> {
  if (!docId || !blobFileName.trim()) return;

  try {
    await DashboardOrchService.updateDocumentDownloadDate(docId, blobFileName, "FILE");
  } catch (error) {
    console.error("Failed to update download date:", error);
  }
}

function resolveDownloadBlobFileName(
  musicId: string | null | undefined,
  format: string,
  fallbackFileName: string,
): string {
  if (musicId?.trim()) {
    return buildMusicBlobStorageFileName(musicId, format);
  }
  return fallbackFileName;
}

async function updateMusicDocProfileAfterConversion(
  docId: string,
  musicId: string,
  targetFormat: string,
  resultPath: string,
): Promise<void> {
  const userGuid = normalizeGuid(readCookie("userguid"));
  const profileRes = await docProfileApi.GetDocProfile(userGuid, docId);
  const docProfile = (profileRes as { data?: Record<string, unknown> }).data ?? profileRes;
  if (!docProfile || typeof docProfile !== "object") {
    throw new Error(`Failed to fetch doc profile ${docId}`);
  }

  const modifiedName = buildMusicBlobStorageFileName(musicId, targetFormat);
  const resultRef = parseMusicBlobRefFromPath(resultPath, getBlobContainer());
  const modifieldFileUrl = resultPath.startsWith("http")
    ? resultPath.split("?")[0]
    : `${getBlobUri()}pdfsimpliusers/${resultRef.folder}/${resultRef.fileName}`;

  const profile = docProfile as Record<string, unknown>;
  profile.modifiedName = modifiedName;
  profile.ModifiedName = modifiedName;
  profile.modifieldFileUrl = modifieldFileUrl;
  profile.ModifieldFileUrl = modifieldFileUrl;
  profile.convertedFileFormat = targetFormat;
  profile.ConvertedFileFormat = targetFormat;
  profile.hasDocumentConverted = true;
  profile.HasDocumentConverted = true;
  profile.modifiedDateTime = new Date().toISOString();
  profile.ModifiedDateTime = profile.modifiedDateTime;

  await docProfileApi.UpdateDocProfile(userGuid, normalizeDocProfileForApi(profile));
}

export function buildMusicBlobUrl(relativePath: string, sasToken: string): string {
  return `${getBlobUri()}pdfsimpliusers/${relativePath}${sasToken}`;
}

export function resolveStemsZipRef(
  music: Pick<GenerateMusicResult, "blobContainer">,
  stemsZipName: string,
): StemsZipRef {
  if (!music.blobContainer) {
    throw new Error("Blob container is required.");
  }
  if (!stemsZipName) {
    throw new Error("Stems archive filename is required.");
  }

  return {
    blob_container: music.blobContainer,
    filename: stemsZipName,
  };
}

/** Fetches the stems ZIP from blob storage (same SAS pattern as song download). */
export async function fetchStemsZipFromBlob(
  stemsZip: StemsZipRef,
): Promise<Blob> {
  const relativePath = `${stemsZip.blob_container}/${stemsZip.filename}`;
  const sasToken = await getSasTokenForBlobAccess();
  const response = await fetch(buildMusicBlobUrl(relativePath, sasToken));
  if (!response.ok) {
    // throw new Error("Failed to download stems archive from blob storage.");
    throw new Error("Please try again.");
  }
  return response.blob();
}

/**
 * Builds doc-profile / querydata custom props for the stems archive.
 * Key is `zip`; value is the archive filename only (`{jobId}_stems.zip`).
 */
export function buildStemsZipDocProps(
  stemsZipName: string,
): Record<string, string> {
  return { zip: stemsZipName };
}

/**
 * Builds doc-operation payload aligned with text-to-speech flow
 * (DocOperationRequestForTextToSpeech in WorkSimpli login).
 */
export function buildMusicDocOperationPayload(options: {
  docOperationId: string;
  docId: string;
  folder: string;
  fileName: string;
  fromFormat: string;
  toFormat: string;
  visitId: string;
  visitorId: string;
  category: string;
  responseFileName?: string;
}): Record<string, unknown> {
  const relativePath = `${options.folder}/${options.fileName}`;
  const filePath = `${getBlobUri()}pdfsimpliusers/${relativePath}`;

  return {
    DocOperationID: options.docOperationId,
    DocId: options.docId,
    FromFormat: getDocOperationFileType(options.fromFormat),
    ToFormat: getDocOperationFileType(options.toFormat),
    OperationType: DocOperationType.CONVERT,
    BlobOutputFolder: getCleanUserGuid(),
    ExistingBlobContainer: options.folder,
    DocBlobPaths: [filePath],
    FileName: options.fileName,
    ResponseFileName: options.responseFileName ?? "",
    DocCategoryName: options.category,
    GeneralContext: {
      VisitId: options.visitId,
      VisitorId: options.visitorId,
      ABTestResult: null,
    },
    isScannedFile: false,
  };
}

async function submitMusicDocOperation(
  options: SaveMusicExportOptions,
  folder: string,
  fileName: string,
  visitId: string,
  visitorId: string,
  docId?: string,
): Promise<void> {
  if (!options.convertedFormat) return;

  const responseFileName =
    options.music.musicId && options.convertedFormat
      ? buildMusicBlobStorageFileName(options.music.musicId, options.convertedFormat)
      : "";

  const payload = buildMusicDocOperationPayload({
    docOperationId: createUUID(),
    docId: docId ?? createUUID(),
    folder,
    fileName,
    fromFormat: fileName.split(".").pop()?.toLowerCase() ?? "mp3",
    toFormat: options.convertedFormat,
    visitId,
    visitorId,
    category: CategoryName.MUSIC_GENERATOR_AI,
    responseFileName,
  });

  const response = await docOperationApi.createDocOperation(
    payload,
    options.authIntent ?? (isUserLoggedIn() ? "signin" : "signup"),
  );
  if (!response.ok) {
    const detail = await response.text().catch(() => "");
    throw new Error(
      `doc-operation failed: ${response.status}${detail ? ` - ${detail}` : ""}`,
    );
  }
}

async function createMusicDocProfile(
  music: Pick<
    GenerateMusicResult,
    "musicId" | "filename" | "blobPath" | "blobContainer" | "metadataFileName"
  >,
  category: string,
): Promise<string | undefined> {
  const userGuid = getCleanUserGuid();
  const displayName = music.filename;
  const { folder, fileName: blobStorageFileName } = resolveMusicStorageRef(music);
  const fileUrl = `${getBlobUri()}pdfsimpliusers/${folder}/${blobStorageFileName}`;
  const blobFormat = getFileExtension(blobStorageFileName) ?? "wav";
  const docId = createUUID();

  const customData = music.musicId
    ? serializeCustomDataForDocProfile(getMusicGenerationCustomData(music.musicId))
    : undefined;

  // OriginalName = MusicGeneration_{timestamp}.wav (saved display name).
  // UniqueName + ModifiedName = {jobId}.wav (blob). Dashboard rename updates DisplayName only.
  const docProfileData = {
    Id: docId,
    OrignialDocumentId: docId,
    OriginalName: displayName,
    OriginalFileUrl: fileUrl,
    OriginalFileFormat: blobFormat,
    UniqueName: blobStorageFileName,
    UploadDateTime: new Date().toISOString(),
    ConvertedFileFormat: blobFormat,
    ModifiedName: blobStorageFileName,
    ModifieldFileUrl: fileUrl,
    ModifiedDateTime: new Date().toISOString(),
    HasDocumentConverted: true,
    TenantId: getTenantId(),
    SizeInKB: 1560,
    isForm: false,
    IsWelcomeDocument: false,
    IsSignSimipliDoc: false,
    IsAdvanceFormatDoc: false,
    CategoryName: category,
    customProps: {
      ...(music.musicId && { musicJobId: music.musicId }),
      ...(music.metadataFileName && { compositionPlan: music.metadataFileName }),
      ...(customData && { customData }),
    },
  };

  const docProfileResponse = await docProfileApi.createDocProfile(
    userGuid,
    normalizeDocProfileForApi(docProfileData as Record<string, unknown>),
    "false",
  );
  return (docProfileResponse as { data?: { id?: string } })?.data?.id ?? docId;
}

/** Creates doc profile for onboarding/guest users (gift funnel before signup). */
export async function registerOnboardingMusicDocProfile(
  music: Pick<
    GenerateMusicResult,
    "musicId" | "filename" | "blobPath" | "blobContainer" | "metadataFileName"
  >,
): Promise<string | undefined> {
  if (!music.musicId) {
    return undefined;
  }

  const existing = registeredDocProfileIds.get(music.musicId);
  if (existing) {
    return existing;
  }

  const category = CategoryName.MUSIC_GENERATOR_AI;
  const docId = await createMusicDocProfile(music, category);
  if (docId && music.musicId) {
    registeredDocProfileIds.set(music.musicId, docId);
  }
  return docId;
}

/** Creates the doc profile once when music generation completes (logged-in users). */
export async function registerMusicDocProfile(
  music: Pick<
    GenerateMusicResult,
    "musicId" | "filename" | "blobPath" | "blobContainer" | "metadataFileName"
  >,
): Promise<string | undefined> {
  if (!isUserLoggedIn() || !music.musicId) {
    return undefined;
  }

  const existing = registeredDocProfileIds.get(music.musicId);
  if (existing) {
    return existing;
  }

  const category = CategoryName.MUSIC_GENERATOR_AI;
  const docId = await createMusicDocProfile(music, category);
  if (docId && music.musicId) {
    registeredDocProfileIds.set(music.musicId, docId);
  }
  return docId;
}

async function requireMusicDocProfileId(
  music: Pick<
    GenerateMusicResult,
    "musicId" | "filename" | "blobPath" | "blobContainer"
  >,
): Promise<string> {
  const docId =
    (music.musicId && registeredDocProfileIds.get(music.musicId)) ||
    (await registerMusicDocProfile(music));
  if (!docId) {
    throw new Error("Failed to resolve document profile.");
  }
  return docId;
}

/** Resolves doc profile id and source audio filename for stem separation. */
export async function resolveStemSeparationDocSource(
  musicId: string,
): Promise<{ docId: string; fileName: string }> {
  const docId = await requireMusicDocProfileId({
    musicId,
    filename: "",
    blobContainer: getBlobContainer(),
  });
  const userGuid = normalizeGuid(readCookie("userguid"));
  const profileRes = await docProfileApi.GetDocProfile(userGuid, docId);
  const docProfile = (profileRes as { data?: Record<string, unknown> }).data ?? profileRes;
  const profile = docProfile as Record<string, unknown>;

  const blobFileName = resolveBlobFileNameFromDocProfile(profile);

  if (blobFileName) {
    return { docId, fileName: blobFileName };
  }

  throw new Error("No blob filename found in document profile.");
}

async function convertDocAndResolveDownload(
  docId: string,
  musicId: string | undefined,
  origFormat: string,
  targetFormat: string,
  visitId: string,
  visitorId: string,
  fallbackFileName: string,
  relativeUrl: string,
): Promise<{ downloadUrl: string; fileName: string }> {
  if (origFormat.toLowerCase() === targetFormat.toLowerCase()) {
    const sasToken = await getSasTokenForBlobAccess();
    const downloadUrl = buildMusicBlobUrl(relativeUrl, sasToken);
    const downloadName =
      musicId?.trim()
        ? buildMusicBlobStorageFileName(musicId, targetFormat)
        : fallbackFileName;
    return { downloadUrl, fileName: downloadName };
  }

  const convertDocResponse = (await DashboardOrchService.convertDoc({
    userId: normalizeGuid(readCookie("userguid")),
    docId,
    fromType: origFormat.toUpperCase(),
    toType: targetFormat.toUpperCase(),
    itemType: "FILE",
    fileType: `.${origFormat}`,
    operationName: "CONVERT",
    GeneralContext: {
      VisitId: visitId,
      VisitorId: visitorId,
      ABTestResult: null,
    },
  })) as { jobId?: string; data?: { jobId?: string } };

  const jobId = convertDocResponse?.jobId ?? convertDocResponse?.data?.jobId;
  if (!jobId) {
    throw new Error("Conversion did not return a job id.");
  }

  const jobResult = await DocOperationJobStatusWithInterval(jobId);
  const resultUrls = getDocOperationResultUrls(jobResult);
  if (!resultUrls.length) {
    throw new Error("Conversion completed but no result file was returned.");
  }

  const sasToken = await getSasTokenForBlobAccess();
  const resultPath = resultUrls[0];
  const convertedName = parseMusicBlobRefFromPath(
    resultPath,
    getBlobContainer(),
  ).fileName;
  const downloadUrl = resultPath.startsWith("http")
    ? `${resultPath.split("?")[0]}${sasToken}`
    : buildMusicBlobUrl(
      resultPath.replace(/^pdfsimpliusers\//, ""),
      sasToken,
    );

  const preferredConvertedName =
    musicId?.trim()
      ? buildMusicBlobStorageFileName(musicId, targetFormat)
      : undefined;
  const downloadName =
    (preferredConvertedName && isMusicBlobStorageFileName(preferredConvertedName)
      ? preferredConvertedName
      : undefined) ||
    (isMusicBlobStorageFileName(convertedName) ? convertedName : undefined) ||
    fallbackFileName.replace(/\.[^/.]+$/, `.${targetFormat}`);

  if (musicId?.trim()) {
    await updateMusicDocProfileAfterConversion(
      docId,
      musicId,
      targetFormat,
      resultPath,
    );
  }

  return { downloadUrl, fileName: downloadName };
}

async function startFormatConversion(
  docId: string,
  musicId: string | undefined,
  origFormat: string,
  targetFormat: string,
  visitId: string,
  visitorId: string,
  fallbackFileName: string,
  relativeUrl: string,
): Promise<{ downloadUrl: string; fileName: string }> {
  return convertDocAndResolveDownload(
    docId,
    musicId,
    origFormat,
    targetFormat,
    visitId,
    visitorId,
    fallbackFileName,
    relativeUrl,
  );
}

async function downloadSameFormatFile(options: {
  music: SaveMusicExportOptions["music"];
  relativeUrl: string;
  fileName: string;
}): Promise<void> {
  if (options.music.audioBlob) {
    triggerDownloadBlob(options.music.audioBlob, options.fileName);
    return;
  }

  const sasToken = await getSasTokenForBlobAccess();
  await triggerDownload(
    buildMusicBlobUrl(options.relativeUrl, sasToken),
    options.fileName,
  );
}

/** Uploads the in-memory audio when equalizer changes may not be on blob yet. */
export async function syncMusicBlobForExport(
  music: Pick<
    GenerateMusicResult,
    "musicId" | "filename" | "blobPath" | "blobContainer" | "audioBlob" | "equalizerRevision"
  >,
): Promise<void> {
  if (!music.audioBlob || !(music.equalizerRevision ?? 0)) {
    return;
  }

  const { folder, fileName } = resolveMusicStorageRef(music);
  await overwriteMusicBlob(`${folder}/${fileName}`, music.audioBlob);
}

export interface SaveMusicExportOptions {
  music: Pick<
    GenerateMusicResult,
    "musicId" | "filename" | "blobPath" | "blobContainer" | "audioBlob" | "equalizerRevision"
  >;
  convertedFormat?: string;
  /** When `"stem"`, downloads the selected stem file; `"stemsZip"` downloads the archive. */
  downloadTarget?: "song" | "stem" | "stemsZip";
  stem?: string;
  /** Stems archive filename (`{jobId}_stems.zip`). */
  stemsZipName?: string;
  authIntent?: "signin" | "signup";
}

export type MusicExportResult =
  | { action: "downloaded" }
  | { action: "redirect" };

export async function saveMusicExportFlow(
  options: SaveMusicExportOptions,
): Promise<MusicExportResult> {
  const { folder, fileName } = resolveMusicStorageRef(options.music);
  const relativeUrl = `${folder}/${fileName}`;
  const category = CategoryName.MUSIC_GENERATOR_AI;
  const localeForRedirect = getLocaleForRedirect();

  const visitId = getVisitGuidForTracking();
  const visitorId =
    readCookie("visitorGuid") ??
    readCookie("visitorid") ??
    readCookie("visitorId") ??
    "";

  const origFormat = fileName.split(".").pop()?.toLowerCase() ?? "mp3";
  const targetFormat = (options.convertedFormat ?? origFormat).toLowerCase();
  const needsConversion = targetFormat !== origFormat;

  if (options.downloadTarget !== "stem" && options.downloadTarget !== "stemsZip") {
    await syncMusicBlobForExport(options.music);
  }

  async function downloadStemFile(stemKey: string): Promise<string> {
    if (!options.stemsZipName) {
      throw new Error("Stems archive filename is required.");
    }
    const stemsZipRef = resolveStemsZipRef(options.music, options.stemsZipName);
    const archiveBlob = await fetchStemsZipFromBlob(stemsZipRef);
    const extractedStems = await extractStemArchive(archiveBlob);
    const stem = extractedStems.find((entry) => entry.key === stemKey);
    if (!stem) {
      throw new Error(`Stem "${stemKey}" was not found in the archive.`);
    }
    await triggerDownloadBlob(stem.blob, stem.filename);
    return stem.filename;
  }

  async function downloadStemsZipFile(): Promise<void> {
    if (!options.stemsZipName) {
      throw new Error("Stems archive filename is required.");
    }
    const stemsZipRef = resolveStemsZipRef(options.music, options.stemsZipName);
    const sasToken = await getSasTokenForBlobAccess();
    const relativePath = `${stemsZipRef.blob_container}/${stemsZipRef.filename}`;
    await triggerDownload(
      buildMusicBlobUrl(relativePath, sasToken),
      stemsZipRef.filename,
    );
  }

  if (isLoggedInForDownload()) {
    const abridgedResponse = await getUserAbridged();
    const isPremium = await resolvePremiumForDownload(abridgedResponse);

    if (!isPremium) {
      redirectTo(getLinkWithLocale(localeForRedirect, "billing/plans"));
      return { action: "redirect" };
    }

    if (options.downloadTarget === "stem" && options.stem) {
      const stemFilename = await downloadStemFile(options.stem);
      const docId = await requireMusicDocProfileId(options.music);
      await recordDownloadDate(docId, stemFilename);
      return { action: "downloaded" };
    }

    if (options.downloadTarget === "stemsZip") {
      await downloadStemsZipFile();
      const docId = await requireMusicDocProfileId(options.music);
      await recordDownloadDate(docId, options.stemsZipName ?? "stems.zip");
      return { action: "downloaded" };
    }

    if (!needsConversion) {
      const downloadName = fileName;
      await downloadSameFormatFile({
        music: options.music,
        relativeUrl,
        fileName: downloadName,
      });

      const docId = await requireMusicDocProfileId(options.music);
      await recordDownloadDate(
        docId,
        resolveDownloadBlobFileName(options.music.musicId, origFormat, fileName),
      );
      return { action: "downloaded" };
    }

    const docId = await requireMusicDocProfileId(options.music);

    const { downloadUrl, fileName: convertedFileName } =
      await startFormatConversion(
        docId,
        options.music.musicId ?? undefined,
        origFormat,
        targetFormat,
        visitId,
        visitorId,
        fileName,
        relativeUrl,
      );

    await triggerDownload(downloadUrl, convertedFileName);
    await recordDownloadDate(
      docId,
      resolveDownloadBlobFileName(options.music.musicId, targetFormat, convertedFileName),
    );

    return { action: "downloaded" };
  }

  if (needsConversion) {
    await submitMusicDocOperation(
      options,
      folder,
      fileName,
      visitId,
      visitorId,
    );
  }

  const saveQueryDataResponse = await saveMusicEditorQueryInfo(options.music, {
    convertedFormat: options.convertedFormat,
    stem: options.stem,
    stemsZipName: options.stemsZipName,
  });
  const redirectUrl =
    options.authIntent === "signin" ? "account/signin" : "account/signup";
  redirectTo(
    getLinkWithLocale(localeForRedirect, redirectUrl) +
    `?querydata=${saveQueryDataResponse}&cname=${category}`,
  );
  return { action: "redirect" };
}

function parseJsonProp(val: unknown): Record<string, unknown> {
  if (typeof val === "string") {
    try {
      return JSON.parse(val) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return val && typeof val === "object" ? (val as Record<string, unknown>) : {};
}

const loadMusicFromDocProfileRequests = new Map<
  string,
  Promise<GenerateMusicResult>
>();

async function loadMusicFromDocProfileInternal(
  docId: string,
  userGuid: string,
): Promise<GenerateMusicResult> {
  if (userGuid) {
    await fetchUserProfileAbridged(userGuid);
  }

  const profileRes = await docProfileApi.GetDocProfile(userGuid, docId);
  const docProfile = (profileRes as any)?.data || profileRes;
  if (!docProfile) {
    throw new Error("Failed to fetch document profile");
  }

  const fileUrl = docProfile.modifieldFileUrl || docProfile.originalFileUrl;

  if (!fileUrl) {
    throw new Error("No file URL found in document profile");
  }

  let audioBlob: Blob = new Blob();
  let audioBlobUrlLocal = "";

  try {
    audioBlob = await fetchBlobFromResultPath(fileUrl);
    audioBlobUrlLocal = URL.createObjectURL(audioBlob);
  } catch (error) {
    console.error("Failed to fetch audio blob from blob storage:", error);
  }

  const customProps = docProfile.customProps || {};
  const musicId = resolveMusicJobIdFromDocProfile(docProfile, customProps);
  const customDataFromProfile = mergeMusicEditorCustomData(
    parseCustomDataFromDocProfileCustomProps(customProps),
    parseDocProfileMetadataCustomProps(customProps),
  );

  let lyricsSections = null;
  let timestamps = null;
  let customDataFromMetadata: ReturnType<typeof parseCustomDataFromMetadataBlob> =
    {};

  if (customProps.compositionPlan) {
    try {
      const metaFileUrl = getBlobFileName(userGuid, customProps.compositionPlan);
      const metadata = await fetchJsonFromResultPath(metaFileUrl);
      const parsed = parseMusicMetadataBlob(metadata);
      lyricsSections = parsed.lyricsSections;
      timestamps = parsed.timestamps;
      customDataFromMetadata = parseCustomDataFromMetadataBlob(metadata);
    } catch (error) {
      console.warn("Failed to fetch composition plan from customProps:", error);
    }
  }

  const displayName = resolveDisplayNameFromDocProfile(docProfile);
  if (!displayName) {
    throw new Error("Display name not found in document profile.");
  }
  const filename = displayName;

  const mergedCustomData = mergeMusicEditorCustomData(
    customDataFromProfile,
    customDataFromMetadata,
  );
  const lyricsFromCustomData = parseLyricsSectionsFromCustomData(mergedCustomData.lyrics);
  if (lyricsFromCustomData?.length) {
    lyricsSections = lyricsFromCustomData;
  }
  const editorRestore = buildEditorRestoreFromCustomData(mergedCustomData);

  if (docId && musicId) {
    registeredDocProfileIds.set(musicId, docId);
    if (hasMusicEditorCustomData(mergedCustomData)) {
      registerMusicGenerationCustomData(musicId, mergedCustomData);
    }
  }

  let stemsZipRef = undefined;
  let stemsZipName = undefined;
  if (customProps.stemsArchive) {
    stemsZipName = customProps.stemsArchive;
    stemsZipRef = {
      blob_container: getBlobContainer(),
      filename: stemsZipName,
    };
  }

  const parsedMetadataProps = parseJsonProp(customProps.metadata ?? customProps.Metadata);

  const generatedFormat = resolveGeneratedAudioFormat({
    filename,
    blobPath: fileUrl,
  });

  return {
    audioBlobUrl: audioBlobUrlLocal,
    audioBlob,
    filename,
    audioMimeType: audioMimeTypeForFormat(generatedFormat),
    lyricsSections,
    timestamps,
    musicId,
    blobContainer: getUserContainer(),
    blobPath: fileUrl,
    uploadStatus: "uploaded" as const,
    metadataFileName:
      (typeof customProps.compositionPlan === "string" &&
        customProps.compositionPlan) ||
      undefined,
    stemsZipRef,
    stemsZipName,
    customData: {
      ...parsedMetadataProps,
      ...mergedCustomData,
    },
    ...(editorRestore && { editorRestore }),
  };
}

export async function loadMusicFromDocProfile(
  docId: string,
): Promise<GenerateMusicResult> {
  const userGuid = normalizeGuid(readCookie("userguid"));
  const cacheKey = `${userGuid}:${docId}`;

  const inFlight = loadMusicFromDocProfileRequests.get(cacheKey);
  if (inFlight) {
    return inFlight;
  }

  const request = loadMusicFromDocProfileInternal(docId, userGuid).finally(() => {
    loadMusicFromDocProfileRequests.delete(cacheKey);
  });

  loadMusicFromDocProfileRequests.set(cacheKey, request);
  return request;
}

export async function updateMusicDocProfileDisplayName(
  musicId: string,
  displayName: string,
): Promise<void> {
  const docId = getRegisteredMusicDocProfileId(musicId);
  if (!docId) {
    return;
  }

  const trimmed = displayName.trim();
  if (!trimmed) {
    return;
  }

  try {
    const userGuid = normalizeGuid(readCookie("userguid"));
    const profileRes = await docProfileApi.GetDocProfile(userGuid, docId);
    const docProfile = (profileRes as { data?: Record<string, unknown> }).data ?? profileRes;
    if (!docProfile || typeof docProfile !== "object") {
      throw new Error(`Failed to fetch doc profile ${docId}`);
    }

    const profile = docProfile as Record<string, unknown>;
    profile.displayName = trimmed;
    profile.DisplayName = trimmed;
    profile.modifiedDateTime = new Date().toISOString();
    profile.ModifiedDateTime = profile.modifiedDateTime;

    await docProfileApi.UpdateDocProfile(userGuid, normalizeDocProfileForApi(profile));
  } catch (error) {
    console.error("Failed to update doc profile display name:", error);
    reportApiError(error);
  }
}

export async function updateMusicDocProfileWithStems(
  musicId: string,
  stemsZipName: string,
): Promise<void> {
  const docId = getRegisteredMusicDocProfileId(musicId);
  if (!docId) {
    console.warn(`No registered doc profile ID found for music ID: ${musicId}`);
    return;
  }

  try {
    const userGuid = normalizeGuid(readCookie("userguid"));
    const profileRes = await docProfileApi.GetDocProfile(userGuid, docId);
    const docProfile = (profileRes as any)?.data || profileRes;
    if (!docProfile) {
      throw new Error(`Failed to fetch doc profile ${docId}`);
    }

    const existingProps = docProfile.customProps || {};
    const updatedProps = {
      ...existingProps,
      stemsArchive: stemsZipName,
    };
    docProfile.customProps = updatedProps;

    await docProfileApi.UpdateDocProfile(
      userGuid,
      normalizeDocProfileForApi(docProfile as Record<string, unknown>),
    );
  } catch (error) {
    console.error("Failed to update doc profile with stems ZIP:", error);
    reportApiError(error);
  }
}
